import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Loader2, ShieldCheck } from 'lucide-react';
import { DocumentVisibility } from './types';
import { VisibilityField } from './VisibilityField';

interface BulkPermissionsForm {
  visibility: DocumentVisibility;
  shared_with_company_ids: number[];
  requires_acceptance: boolean;
}

interface BulkPermissionsDialogProps {
  open: boolean;
  count: number;
  saving: boolean;
  form: BulkPermissionsForm;
  onFormChange: (form: BulkPermissionsForm) => void;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
}

/**
 * Set visibility / sharing / read-&-accept on several documents at once.
 * Applies the chosen permissions to every selected document, replacing
 * their current sharing state (an explicit set, not a merge).
 */
export function BulkPermissionsDialog({
  open,
  count,
  saving,
  form,
  onFormChange,
  onClose,
  onSubmit,
}: BulkPermissionsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Set permissions for {count} document{count === 1 ? '' : 's'}</DialogTitle>
          <DialogDescription>
            The visibility and sharing you choose here replaces the current
            permissions on every selected document.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <VisibilityField
            visibility={form.visibility}
            onVisibilityChange={(v) => onFormChange({ ...form, visibility: v })}
            sharedCompanyIds={form.shared_with_company_ids}
            onSharedCompanyIdsChange={(ids) => onFormChange({ ...form, shared_with_company_ids: ids })}
          />

          <label className="flex items-start gap-3 cursor-pointer rounded-md border p-3 hover:bg-muted/30">
            <input
              type="checkbox"
              checked={form.requires_acceptance}
              onChange={(e) => onFormChange({ ...form, requires_acceptance: e.target.checked })}
              className="mt-0.5 cursor-pointer"
            />
            <div>
              <p className="text-sm font-medium">Require Read &amp; Accept Receipt</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Recipients must open each document and confirm acceptance.
                Selected suppliers (Shared visibility) get notified automatically.
              </p>
            </div>
          </label>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Applying…
                </>
              ) : (
                <>
                  <ShieldCheck className="h-4 w-4 mr-2" />
                  Apply to {count} document{count === 1 ? '' : 's'}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
